import { Fragment } from 'react';
import {
    Button,
    Dimmer,
    Header,
    Loader,
    Modal,
    Segment,
    Table,
} from 'semantic-ui-react';

import {
    SYSTEM_TYPE_NEW,
    displayRepeaterSystemId,
    USB_KEY_ITEM_FIELDS,
} from '../../../Constants/gen1Constants';
import { getGen1ApplicationTypeLabel } from '../gen1ApplicationType';

// USB Keys 表示用（Gen1Application と同様）
const USB_KEY_DISPLAY = {
    Grade1UnlimitedAccessUsbKeyQty: {
        code: 'KPGKEY01',
        label: 'Grade-1 Unlimited Access USB Key',
    },
    Grade2LimitedAccessUsbKeyQty: {
        code: 'KPGKEY02',
        label: 'Grade-2 Limited Access USB Key',
    },
    SystemManagerSoftwareWithGrade1KeyQty: {
        code: 'KPGKEYSM',
        label: 'System Manager Software with Grade-1 Key',
    },
    SystemManagerSoftwareQty: {
        code: 'KPG-110SMK',
        label: 'System Manager Software',
    },
};

/**
 * 申請内容のプレビューを表示するモーダル。
 * Order ボタンで申請確定処理を実行する。
 */
export default function Gen1ApplicationPreviewModal({
    open,
    onClose,
    applicationState,
    onOrderClick,
    isSubmitting = false,
    orderDisabled = false,
}) {
    const order = applicationState?.gen1Order ?? {};
    const item = order.Gen1TrunkingOrderItem ?? {};
    const repeaterSerials = order.Gen1TrunkingOrderRepeaterSerials ?? [];
    const previewOtherSystemId = applicationState?.otherSystemId ?? '';
    const addRepeater = applicationState?.addRepeater === true;
    const repeaterTrunkingQty = Number(item.RepeaterQty) || 0;
    const exchangeRepeaterQty = Number(item.ExchangeRepeaterPairQty) || 0;
    const removeRepeaterQty = Number(item.RemoveRepeaterQty) || 0;

    const addCount = addRepeater ? Math.max(0, repeaterTrunkingQty) : 0;
    const exchangePairCount = Math.max(0, exchangeRepeaterQty);
    const removeCount = Math.max(0, removeRepeaterQty);
    const addLines = repeaterSerials.slice(0, addCount);
    const exchangeLines = repeaterSerials.slice(
        addCount,
        addCount + exchangePairCount * 2
    );
    const removeLines = repeaterSerials.slice(
        addCount + exchangePairCount * 2,
        addCount + exchangePairCount * 2 + removeCount
    );

    const exchangePairs = Array.from({ length: exchangePairCount }, (_, i) => ({
        removeLine: exchangeLines[2 * i],
        addLine: exchangeLines[2 * i + 1],
        pairNumber: i + 1,
    })).filter((p) => p.removeLine && p.addLine);

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="large"
            closeOnDimmerClick={false}
        >
            <Modal.Header>Application Preview</Modal.Header>
            <Modal.Content scrolling>
                <Segment>
                    <Header
                        as="h4"
                        icon="building"
                        content="Customer Information"
                        dividing
                    />
                    <Table definition size="small" celled>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell width={4}>Contact Name</Table.Cell>
                                <Table.Cell>
                                    {order.ContactName ?? '-'}
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>Contact Email</Table.Cell>
                                <Table.Cell>
                                    {order.ContactEmail ?? '-'}
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>Address</Table.Cell>
                                <Table.Cell>
                                    {[
                                        order.AddressLineOne,
                                        order.AddressLineTwo,
                                        order.AddressLineThree,
                                    ]
                                        .filter(Boolean)
                                        .join(', ') || '-'}
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>City / State / Zip</Table.Cell>
                                <Table.Cell>
                                    {[order.City, order.State, order.Zip]
                                        .filter(Boolean)
                                        .join(', ') || '-'}
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>Country</Table.Cell>
                                <Table.Cell>{order.Country ?? '-'}</Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>Telephone</Table.Cell>
                                <Table.Cell>
                                    {order.Telephone ?? '-'}
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>Manufacturers Rep</Table.Cell>
                                <Table.Cell>
                                    {order.ManufactureRep ?? '-'}
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Segment>

                <Segment>
                    <Header
                        as="h4"
                        icon="wifi"
                        content="Gen1 Trunking Information"
                        dividing
                    />
                    <Table definition size="small" celled>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell width={4}>
                                    Dealer Order Number
                                </Table.Cell>
                                <Table.Cell>
                                    {(order.DealerOrderNumber ?? '').trim() ||
                                        '-'}
                                </Table.Cell>
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell width={4}>System Type</Table.Cell>
                                <Table.Cell>
                                    {order.SystemType ?? '-'}
                                </Table.Cell>
                            </Table.Row>
                            {order.SystemType !== SYSTEM_TYPE_NEW && (
                                <Table.Row>
                                    <Table.Cell>System ID</Table.Cell>
                                    <Table.Cell>
                                        {order.SystemId ?? '-'}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                            <Table.Row>
                                <Table.Cell>Application Type</Table.Cell>
                                <Table.Cell>
                                    {getGen1ApplicationTypeLabel(
                                        order.IncludePurchasedRepeaters
                                    )}
                                </Table.Cell>
                            </Table.Row>
                            {order.IncludePurchasedRepeaters &&
                                applicationState?.orderNumber && (
                                    <Table.Row>
                                        <Table.Cell>
                                            Sales Order Number (SO#)
                                        </Table.Cell>
                                        <Table.Cell>
                                            {applicationState.orderNumber}
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            {addRepeater && (
                                <>
                                    <Table.Row>
                                        <Table.Cell>
                                            Repeater Trunking (RepeaterQty)
                                        </Table.Cell>
                                        <Table.Cell>
                                            {repeaterTrunkingQty}
                                        </Table.Cell>
                                    </Table.Row>
                                    <Table.Row>
                                        <Table.Cell>
                                            Multi-site Networking
                                        </Table.Cell>
                                        <Table.Cell>
                                            {Number(
                                                item.MultiSiteNetworkingQty
                                            ) || 0}
                                        </Table.Cell>
                                    </Table.Row>
                                </>
                            )}
                            {exchangeRepeaterQty > 0 && (
                                <>
                                    <Table.Row>
                                        <Table.Cell>
                                            Exchange Repeater Pair Qty
                                        </Table.Cell>
                                        <Table.Cell>
                                            {exchangeRepeaterQty}
                                        </Table.Cell>
                                    </Table.Row>
                                    {applicationState?.otherSystemId && (
                                        <Table.Row>
                                            <Table.Cell>
                                                Other System ID
                                            </Table.Cell>
                                            <Table.Cell>
                                                {applicationState.otherSystemId}
                                            </Table.Cell>
                                        </Table.Row>
                                    )}
                                </>
                            )}
                            {removeRepeaterQty > 0 && (
                                <Table.Row>
                                    <Table.Cell>Remove Repeater Qty</Table.Cell>
                                    <Table.Cell>{removeRepeaterQty}</Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Segment>

                {repeaterSerials.length > 0 && (
                    <Segment>
                        <Header as="h4" content="Repeater Details" dividing />
                        <Table celled striped size="small">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>
                                        Function
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>
                                        System ID
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>Model</Table.HeaderCell>
                                    <Table.HeaderCell>
                                        Serial Number
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {addLines.map((row) => (
                                    <Table.Row key={row.LineSequenceNumber}>
                                        <Table.Cell>
                                            {row.OperationType ?? '-'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {displayRepeaterSystemId(
                                                row,
                                                order.SystemType,
                                                order.SystemId,
                                                previewOtherSystemId
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {row.BaseModel ?? '-'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {row.SerialNumber ?? '-'}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                                {exchangePairs.map(
                                    ({ removeLine, addLine, pairNumber }) => (
                                        <Fragment key={pairNumber}>
                                            <Table.Row>
                                                <Table.Cell
                                                    colSpan={4}
                                                    style={{
                                                        backgroundColor:
                                                            '#f0f0f0',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    Exchange Pair {pairNumber}
                                                </Table.Cell>
                                            </Table.Row>
                                            <Table.Row>
                                                <Table.Cell>
                                                    {removeLine.OperationType ??
                                                        '-'}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {displayRepeaterSystemId(
                                                        removeLine,
                                                        order.SystemType,
                                                        order.SystemId,
                                                        previewOtherSystemId
                                                    )}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {removeLine.BaseModel ??
                                                        '-'}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {removeLine.SerialNumber ??
                                                        '-'}
                                                </Table.Cell>
                                            </Table.Row>
                                            <Table.Row>
                                                <Table.Cell>
                                                    {addLine.OperationType ??
                                                        '-'}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {displayRepeaterSystemId(
                                                        addLine,
                                                        order.SystemType,
                                                        order.SystemId,
                                                        previewOtherSystemId
                                                    )}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {addLine.BaseModel ?? '-'}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {addLine.SerialNumber ??
                                                        '-'}
                                                </Table.Cell>
                                            </Table.Row>
                                        </Fragment>
                                    )
                                )}
                                {removeLines.map((row) => (
                                    <Table.Row key={row.LineSequenceNumber}>
                                        <Table.Cell>
                                            {row.OperationType ?? '-'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {displayRepeaterSystemId(
                                                row,
                                                order.SystemType,
                                                order.SystemId,
                                                previewOtherSystemId
                                            )}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {row.BaseModel ?? '-'}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {row.SerialNumber ?? '-'}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </Segment>
                )}

                <Segment>
                    <Header as="h4" content="USB Keys and Software" dividing />
                    {item.isNeededUsbKeyAndSoftware ? (
                        <Table definition size="small" celled>
                            <Table.Body>
                                {(() => {
                                    const rows = USB_KEY_ITEM_FIELDS.filter(
                                        (itemField) =>
                                            (Number(item[itemField]) || 0) > 0
                                    );
                                    if (rows.length === 0) {
                                        return (
                                            <Table.Row key="usb-empty">
                                                <Table.Cell colSpan={2}>
                                                    No USB or software lines
                                                    with quantity greater than
                                                    0.
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    }
                                    return rows.map((itemField) => {
                                        const display =
                                            USB_KEY_DISPLAY[itemField];
                                        const qty =
                                            Number(item[itemField]) || 0;
                                        return (
                                            <Table.Row key={itemField}>
                                                <Table.Cell width={6}>
                                                    {display?.code} —{' '}
                                                    {display?.label}
                                                </Table.Cell>
                                                <Table.Cell>{qty}</Table.Cell>
                                            </Table.Row>
                                        );
                                    });
                                })()}
                            </Table.Body>
                        </Table>
                    ) : (
                        <p>None</p>
                    )}
                </Segment>

                {(order.AdditionalNotes ?? '').trim() && (
                    <Segment>
                        <Header as="h4" content="Additional Notes" dividing />
                        <p style={{ whiteSpace: 'pre-wrap' }}>
                            {order.AdditionalNotes}
                        </p>
                    </Segment>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button
                    content="Close"
                    onClick={onClose}
                    disabled={isSubmitting}
                />
                <Button
                    content="Order"
                    icon="check"
                    color="blue"
                    disabled={isSubmitting || orderDisabled}
                    onClick={onOrderClick}
                />
            </Modal.Actions>
            <Dimmer active={isSubmitting}>
                <Loader content="Loading..." size="huge" />
            </Dimmer>
        </Modal>
    );
}
